import React, { useState } from "react";
import { useDeleteLessonMutation } from "../../services/courses";
import { CreateLessonModal } from "./CreateLessonModal";
import { EditLessonModal } from "./EditLessonModal";

interface Lesson {
  _id: string;
  title: string;
  description: string;
  videoPath: string;
  hasWorksheet: boolean;
  worksheetPath: string;
  duration?: number;
  order?: number;
}

interface Module {
  _id: string;
  title: string;
  description: string;
  order?: number;
  lessons: Lesson[];
}

interface ManageLessonsModalProps {
  courseId: string;
  module: Module;
  onClose: () => void;
}

export const ManageLessonsModal: React.FC<ManageLessonsModalProps> = ({
  courseId,
  module,
  onClose,
}) => {
  const [deleteLesson] = useDeleteLessonMutation();

  const [showCreateLesson, setShowCreateLesson] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);

  const handleDeleteLesson = async (lessonId: string, lessonTitle: string) => {
    if (
      window.confirm(`Are you sure you want to delete lesson "${lessonTitle}"?`)
    ) {
      try {
        await deleteLesson({
          courseId,
          moduleId: module._id,
          lessonId,
        }).unwrap();
        alert("Lesson deleted successfully");
      } catch (err) {
        alert("Failed to delete lesson");
        console.error(err);
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const sortedLessons = [...(module.lessons || [])].sort((a, b) => {
    if (a.order !== undefined && b.order !== undefined)
      return a.order - b.order;
    if (a.order !== undefined) return -1;
    if (b.order !== undefined) return 1;
    return 0;
  });

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
        onClick={handleBackdropClick}
      >
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {module.title}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Manage lessons in this module
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4"
              >
                ×
              </button>
            </div>
            <button
              onClick={() => setShowCreateLesson(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              + Add New Lesson
            </button>
          </div>

          <div className="p-6">
            {sortedLessons.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🎥</div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No lessons yet
                </h3>
                <p className="text-gray-500 mb-6">
                  Add your first lesson to this module
                </p>
                <button
                  onClick={() => setShowCreateLesson(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-block"
                >
                  Create First Lesson
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedLessons.map((lesson, index) => (
                  <div
                    key={lesson._id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="bg-blue-100 text-blue-800 rounded-full w-10 h-10 flex items-center justify-center font-semibold">
                          {index + 1}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 truncate">
                              {lesson.title}
                            </h3>
                            {lesson.description && (
                              <div
                                className="text-sm text-gray-600 mt-1 line-clamp-3"
                                dangerouslySetInnerHTML={{
                                  __html: lesson.description,
                                }}
                              />
                            )}
                          </div>
                          {lesson.order !== undefined && (
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              Order: {lesson.order}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-3 text-sm text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            ⏱️ {formatDuration(lesson.duration)}
                          </span>
                          {lesson.videoPath && (
                            <span className="flex items-center gap-1">
                              🎥 Video
                            </span>
                          )}
                          {lesson.hasWorksheet && (
                            <span className="flex items-center gap-1">
                              📄 Worksheet
                            </span>
                          )}
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingLesson(lesson)}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded text-sm font-medium transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteLesson(lesson._id, lesson.title)
                            }
                            className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1.5 rounded text-sm font-medium transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>

                    {(lesson.videoPath || lesson.worksheetPath) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <details className="text-xs text-gray-500">
                          <summary className="cursor-pointer hover:text-gray-700">
                            Show paths
                          </summary>
                          <div className="mt-2 space-y-1 pl-4">
                            {lesson.videoPath && (
                              <div>
                                <strong>Video:</strong>{" "}
                                <code className="bg-gray-100 px-1 rounded">
                                  {lesson.videoPath}
                                </code>
                              </div>
                            )}
                            {lesson.worksheetPath && (
                              <div>
                                <strong>Worksheet:</strong>{" "}
                                <code className="bg-gray-100 px-1 rounded">
                                  {lesson.worksheetPath}
                                </code>
                              </div>
                            )}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {showCreateLesson && (
        <CreateLessonModal
          courseId={courseId}
          moduleId={module._id}
          onClose={() => setShowCreateLesson(false)}
        />
      )}

      {editingLesson && (
        <EditLessonModal
          courseId={courseId}
          moduleId={module._id}
          lesson={editingLesson}
          onClose={() => setEditingLesson(null)}
        />
      )}
    </>
  );
};
